import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormInstance, Modal, Tooltip } from 'antd';
import { CancelButton } from 'components/buttons/cancel-button';
import { EditButton } from 'components/buttons/edit-button';
import { SaveButton } from 'components/buttons/save-button';
import { useForm } from 'hooks/use-form';

type UseBlockEditModeParams = {
  formName: string;
  onSubmit?: (values: object) => Promise<void>;
};
type UseBlockEditModeReturn = {
  buttons: React.ReactNode[];
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  form: FormInstance;
  formName: string;
};

type UseBlockEditMode = (
  params: UseBlockEditModeParams,
) => UseBlockEditModeReturn;

type CurrentEditingBlockContextType = {
  currentEditingBlock?: string;
  setCurrentEditingBlock: (_: string | undefined) => void;
};

export const CurrentEditingBlockContext =
  React.createContext<CurrentEditingBlockContextType>({
    currentEditingBlock: undefined,
    setCurrentEditingBlock: (_: string | undefined) => {},
  });

export const useCurrentEditingBlock = (): CurrentEditingBlockContextType => {
  const { currentEditingBlock, setCurrentEditingBlock } = useContext(
    CurrentEditingBlockContext,
  );

  const onReload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = 'Unsaved data will be lost';
  }, []);

  useEffect(() => {
    if (currentEditingBlock) {
      window.addEventListener('beforeunload', onReload);
    }

    return () => window.removeEventListener('beforeunload', onReload);
  }, [currentEditingBlock, onReload]);

  return { currentEditingBlock, setCurrentEditingBlock };
};

export const useBlockEditMode: UseBlockEditMode = ({ formName, onSubmit }) => {
  const { form } = useForm({ onSubmit });

  const [isEditMode, setIsEditMode] = useState(false);

  const { setCurrentEditingBlock, currentEditingBlock } =
    useCurrentEditingBlock();

  useEffect(() => {
    form.resetFields();
  }, [isEditMode, form]);

  const onCancelClick = (): void => {
    Modal.confirm({
      content: 'Changes you made so far will not be saved',
      okText: 'Yes, quit',
      onOk: () => {
        setCurrentEditingBlock(undefined);
        setIsEditMode(false);
      },
      title: 'Quit editing?',
    });
  };
  const onEditClick = (): void => {
    setCurrentEditingBlock(formName);
    setIsEditMode(true);
  };

  const disabled =
    currentEditingBlock !== undefined && currentEditingBlock !== formName;

  const buttons: React.ReactNode[] = isEditMode
    ? [
        <CancelButton
          key="cancel-button"
          onClick={onCancelClick}
          disabled={disabled}
        />,
        <SaveButton
          key="save-button"
          htmlType="submit"
          form={formName}
          disabled={disabled}
        />,
      ]
    : [
        disabled ? (
          <Tooltip
            key="tooltip"
            placement="bottom"
            title="Complete editing in another section to modify this"
          >
            <div>
              <EditButton
                key={'edit-button'}
                onClick={onEditClick}
                disabled={disabled}
              />
            </div>
          </Tooltip>
        ) : (
          <EditButton key={'edit-button'} onClick={onEditClick} />
        ),
      ];

  return {
    buttons,
    form,
    formName,
    isEditMode,
    setIsEditMode: (value: boolean) => {
      if (!value && currentEditingBlock === formName) {
        setCurrentEditingBlock(undefined);
      }

      setIsEditMode(value);
    },
  };
};
